import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Simple, self-contained pagination bar.
 *  - `page`: current 1-indexed page
 *  - `total`: total item count (already filtered)
 *  - `pageSize`: items per page
 *  - `onChange(nextPage)`: called when the page changes
 */
export default function Pagination({ page, total, pageSize, onChange, testidPrefix = "pagination" }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clamp = (p) => Math.min(Math.max(1, p), pageCount);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  if (total <= pageSize) return null;

  return (
    <div className="flex items-center justify-between border-t-2 border-[#09090B] bg-neutral-50 px-4 py-2" data-testid={testidPrefix}>
      <div className="text-xs font-mono uppercase tracking-widest text-neutral-600" data-testid={`${testidPrefix}-info`}>
        {start.toLocaleString("tr-TR")}–{end.toLocaleString("tr-TR")} / {total.toLocaleString("tr-TR")}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost" size="sm"
          className="rounded-none h-8 w-8 p-0"
          disabled={page <= 1}
          onClick={() => onChange(1)}
          data-testid={`${testidPrefix}-first`}
          aria-label="İlk sayfa"
        >
          <ChevronsLeft size={16} />
        </Button>
        <Button
          variant="ghost" size="sm"
          className="rounded-none h-8 w-8 p-0"
          disabled={page <= 1}
          onClick={() => onChange(clamp(page - 1))}
          data-testid={`${testidPrefix}-prev`}
          aria-label="Önceki sayfa"
        >
          <ChevronLeft size={16} />
        </Button>
        <span className="text-xs font-mono px-3" data-testid={`${testidPrefix}-page`}>
          {page.toLocaleString("tr-TR")} / {pageCount.toLocaleString("tr-TR")}
        </span>
        <Button
          variant="ghost" size="sm"
          className="rounded-none h-8 w-8 p-0"
          disabled={page >= pageCount}
          onClick={() => onChange(clamp(page + 1))}
          data-testid={`${testidPrefix}-next`}
          aria-label="Sonraki sayfa"
        >
          <ChevronRight size={16} />
        </Button>
        <Button
          variant="ghost" size="sm"
          className="rounded-none h-8 w-8 p-0"
          disabled={page >= pageCount}
          onClick={() => onChange(pageCount)}
          data-testid={`${testidPrefix}-last`}
          aria-label="Son sayfa"
        >
          <ChevronsRight size={16} />
        </Button>
      </div>
    </div>
  );
}
